import { escapeRegExp } from "lodash-es";

import Path from "@app/enum/Path";
import { WEB_CLIENT_ID, WEB_TOKEN_SCOPE } from "@app/util/consts";

export const getLegacyAppUrlWithToken = (
  baseOldAppUrl: string,
  token: string | undefined,
  url: string
) => `${baseOldAppUrl}/${token ? `?token=${token}` : ""}#${url}`;

export const getLegacyAppUrlWithCode = (baseOldAppUrl: string, code: string, url: string) =>
  `${baseOldAppUrl}/?mmc_handoff_code=${code}#${url}`;

export const getLegacyAppUrl = (
  baseOldAppUrl: string,
  token: string | undefined,
  url: string,
  code?: string
) =>
  code
    ? getLegacyAppUrlWithCode(baseOldAppUrl, code, url)
    : getLegacyAppUrlWithToken(baseOldAppUrl, token, url);

export const getLegacyLogoutAppUrl = (baseOldAppUrl: string) => `${baseOldAppUrl}/logout.html`;

export const getAppUrlWithToken = (baseAppUrl: string, token: string, url: string) =>
  `${baseAppUrl}/?mmc_token=${token}&mmc_redirect=${encodeURIComponent(url)}`;

export const getAppUrlWithCode = (baseAppUrl: string, code: string, url: string) =>
  `${baseAppUrl}/?mmc_handoff_code=${code}&mmc_redirect=${encodeURIComponent(url)}`;

// prefers the one-time code when the server minted one; falls back to the legacy
// token param when it didn't (best-effort minting, e.g. mid-rollout)
export const getAppUrl = (baseAppUrl: string, url: string, token: string, code?: string) =>
  code ? getAppUrlWithCode(baseAppUrl, code, url) : getAppUrlWithToken(baseAppUrl, token, url);

export const getAuthCallbackUrl = (origin: string) => `${origin}${Path.AUTH_CALLBACK}`;

export const getLoginAppUrl = (
  loginUrl: string,
  {
    codeChallenge,
    failed,
    locale,
    redirectUri,
    state,
  }: {
    codeChallenge: string;
    failed?: boolean;
    locale?: string;
    redirectUri: string;
    state: string;
  }
) => {
  const url = new URL(loginUrl);
  url.search = new URLSearchParams({
    client_id: WEB_CLIENT_ID,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    redirect_uri: redirectUri,
    response_type: "code",
    scope: WEB_TOKEN_SCOPE,
    state,
    ...(failed ? { mmc_sign_in_failed: "1" } : {}),
    ...(locale ? { ui_locales: locale } : {}),
  }).toString();
  return url.href;
};

export const getHandoffReturnUrl = (returnUrl: string, code: string) => {
  const { hash, origin, pathname, search } = new URL(returnUrl);
  return getAppUrlWithCode(origin, code, `${pathname}${search}${hash}`);
};

// A blob: or other wrapping URL reports the origin it wraps, so only plain web URLs count.
export const getWebOrigin = (url: string) => {
  try {
    const { origin, protocol } = new URL(url);
    return protocol === "https:" || protocol === "http:" ? origin : undefined;
  } catch {
    return undefined;
  }
};

// "*" in an allowed origin stands for exactly one subdomain label.
export const isAllowedHandoffReturn = (returnUrl: string, allowedOrigins: string[]) => {
  const origin = getWebOrigin(returnUrl);
  return (
    origin !== undefined &&
    allowedOrigins.some((allowed) =>
      new RegExp(`^${allowed.split("*").map(escapeRegExp).join("[a-z0-9-]+")}$`).test(origin)
    )
  );
};

// BASE_APP_URL is set with or without a scheme.
export const isMainAppHost = (baseAppUrl: string, host: string) => {
  try {
    return new URL(baseAppUrl.includes("://") ? baseAppUrl : `https://${baseAppUrl}`).host === host;
  } catch {
    return false;
  }
};
